import { zodResolver } from '@hookform/resolvers/zod';
import { useGetInfiniteProductsList } from '@shared/api/hooks/pcf/products';
import { ProductResult } from '@shared/api/types';
import { useDebounce } from '@shared/components/data-table/hooks/use-debounce';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { Form } from '@shared/components/ui/form';
import { cn } from '@shared/lib/utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as z from 'zod';

const formSchema = z.object({
  product_a: z.string().optional(),
  product_b: z.string().optional(),
});

type ComparisonSelectorFormValues = z.infer<typeof formSchema>;

interface ComparisonSelectorProps {
  className?: string;
  productADetails?: ProductResult;
  productBDetails?: ProductResult;
}

export function ComparisonSelector({
  className,
  productADetails,
  productBDetails,
}: ComparisonSelectorProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const productA = searchParams.get('product_a') || '';
  const productB = searchParams.get('product_b') || '';

  const form = useForm<ComparisonSelectorFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      product_a: productA,
      product_b: productB,
    },
  });

  // Keep form in sync with URL params (e.g. on navigation)
  useEffect(() => {
    const startValues = form.getValues();
    if (startValues.product_a !== productA) {
      form.setValue('product_a', productA);
    }
    if (startValues.product_b !== productB) {
      form.setValue('product_b', productB);
    }
  }, [productA, productB, form]);

  const { product_a: formProductA, product_b: formProductB } = form.watch();

  const handleProductChange = (
    field: 'product_a' | 'product_b',
    value: string
  ) => {
    const currentParams = new URLSearchParams(searchParams);
    if (value) {
      currentParams.set(field, value);
    } else {
      currentParams.delete(field);
    }
    setSearchParams(currentParams);
  };

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInfiniteProductsList({
    status: ['complete'],
    page_size: 10,
    search: debouncedSearch || undefined,
  });

  // Since we have infinite scroll, the selected product might NOT be in the loaded pages
  // We need to fetch the selected product details separately to ensure it displays correctly

  const options =
    data?.pages
      .flatMap((page) => page.results)
      .map((product) => ({
        value: product.uuid,
        label: product.name,
        endAdornment: product.emissionsCategory && (
          <RatingCloud
            status={product.status}
            rating={product.emissionsCategory as RatingCloudProps['rating']}
            className="ml-auto h-8 w-10"
          />
        ),
      })) || [];

  const getSelectedOption = (
    value: string,
    productResult: ProductResult | undefined,
    currentOptions: typeof options
  ) => {
    if (!value || !productResult) return undefined;

    // Check if it's already in the loaded options
    const inOptions = currentOptions.find((o) => o.value === value);
    if (inOptions) return undefined; // Let the component handle it naturally

    return {
      value: productResult.uuid,
      label: productResult.name,
      endAdornment: productResult.emissionsCategory && (
        <RatingCloud
          status={productResult.status}
          rating={productResult.emissionsCategory as RatingCloudProps['rating']}
          className="ml-auto h-8 w-10"
        />
      ),
    };
  };

  const optionsProductA = options.filter((o) => o.value !== formProductB);
  const optionsProductB = options.filter((o) => o.value !== formProductA);

  return (
    <Form {...form}>
      <form
        className={cn('mb-6 grid grid-cols-1 gap-4 md:grid-cols-2', className)}
      >
        <SelectSearchFormField
          name="product_a"
          pluralName="products"
          label="Product"
          placeholder="Select a product"
          options={optionsProductA}
          loading={isLoading}
          onSearchChange={setSearch}
          isLoadingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          onLoadMore={fetchNextPage}
          selectedOption={getSelectedOption(
            formProductA || '',
            productADetails,
            optionsProductA
          )}
          handleSelect={(val) => handleProductChange('product_a', val)}
        />
        <SelectSearchFormField
          name="product_b"
          pluralName="products"
          label="Compared to"
          placeholder="Select a product"
          options={optionsProductB}
          loading={isLoading}
          onSearchChange={setSearch}
          isLoadingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          onLoadMore={fetchNextPage}
          selectedOption={getSelectedOption(
            formProductB || '',
            productBDetails,
            optionsProductB
          )}
          handleSelect={(val) => handleProductChange('product_b', val)}
        />
      </form>
    </Form>
  );
}
